import React, { useMemo, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { toast } from 'sonner'
import { Activity, Save, Droplet } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import SmartBack from '../components/SmartBack'
import { UPPER, LOWER } from '../components/Odontogram'
import { cn } from '../lib/utils'

// Periodontal charting v1 (Paul 2026-08-23). Row-per-tooth grid capturing 6 sites/tooth:
//   probing depth (mm) + bleeding-on-probing per site, plus mobility & furcation per tooth.
// Live summary (BOP%, sites ≥4/≥6mm, mean PD, deepest). Green 1-3 / amber 4-5 / red ≥6mm.
// GM/recession, CAL, suppuration, plaque are a planned v2 (the JSONB store already allows them).

const BUCCAL  = ['MB', 'B', 'DB']
const LINGUAL = ['ML', 'L', 'DL']
const SITES   = [...BUCCAL, ...LINGUAL]

const pdTone = (v) => {
  if (v === '' || v == null) return 'bg-white text-brand-ink'
  const mm = parseInt(v, 10)
  if (isNaN(mm)) return 'bg-white text-brand-ink'
  if (mm >= 6) return 'bg-red-100 text-red-800 font-semibold'
  if (mm >= 4) return 'bg-amber-100 text-amber-800 font-semibold'
  return 'bg-emerald-50 text-emerald-800'
}

export default function PerioChart({ patient = {}, providers = [], exams = [], current = null }) {
  const today = new Date().toISOString().slice(0, 10)
  const [examDate, setExamDate] = useState(current?.examined_on?.slice(0, 10) || today)
  const [provider, setProvider] = useState(current?.provider_name || '')
  const [notes, setNotes]       = useState(current?.notes || '')
  const [data, setData]         = useState(() => current?.data || {})
  const [saving, setSaving]     = useState(false)

  const setPd = (tooth, site, val) => {
    const v = val.replace(/[^\d]/g, '').slice(0, 2)
    setData((d) => ({ ...d, [tooth]: { ...d[tooth], pd: { ...(d[tooth]?.pd || {}), [site]: v } } }))
  }
  const toggleBop = (tooth, site) => {
    setData((d) => ({ ...d, [tooth]: { ...d[tooth], bop: { ...(d[tooth]?.bop || {}), [site]: !(d[tooth]?.bop?.[site]) } } }))
  }
  const setField = (tooth, key, val) => {
    setData((d) => ({ ...d, [tooth]: { ...d[tooth], [key]: val } }))
  }

  const summary = useMemo(() => {
    const pds = []
    let bop = 0, charted = 0, deepest = null
    for (const [tooth, td] of Object.entries(data)) {
      for (const [site, v] of Object.entries(td?.pd || {})) {
        if (v === '' || v == null) continue
        const mm = parseInt(v, 10)
        if (isNaN(mm)) continue
        charted++; pds.push(mm)
        if (!deepest || mm > deepest.mm) deepest = { mm, tooth, site }
      }
      for (const v of Object.values(td?.bop || {})) if (v) bop++
    }
    const mean = pds.length ? pds.reduce((a, c) => a + c, 0) / pds.length : null
    return {
      charted, bop,
      bopPct: charted ? Math.round((bop / charted) * 100) : 0,
      s4: pds.filter((d) => d >= 4).length,
      s6: pds.filter((d) => d >= 6).length,
      mean: mean == null ? null : Math.round(mean * 10) / 10,
      deepest,
    }
  }, [data])

  const save = () => {
    setSaving(true)
    router.post(`/patients/${patient.id}/perio_exams`, {
      examined_on: examDate, provider_name: provider, notes, data,
    }, {
      onSuccess: () => toast.success('Perio exam saved'),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not save'),
      onFinish: () => setSaving(false),
    })
  }

  return (
    <DashboardLayout>
      <div className="mb-4"><SmartBack fallback={`/patients/${patient.id}`} /></div>

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary">
          <Activity size={18} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">Periodontal chart</h1>
          <p className="text-sm text-brand-muted">
            <Link href={`/patients/${patient.id}`} className="hover:text-brand-ink hover:underline">{patient.name}</Link>
            {' · '}probing depths, bleeding, mobility &amp; furcation
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <label className="text-xs font-medium text-brand-muted">Date
            <input type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)}
              className="ml-1 rounded-lg border border-brand-border px-2 py-1 text-sm text-brand-ink" />
          </label>
          <select value={provider} onChange={(e) => setProvider(e.target.value)}
            className="rounded-lg border border-brand-border px-2 py-1 text-sm text-brand-ink" title="Examining dentist">
            <option value="">— dentist —</option>
            {providers.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-primary-dark disabled:opacity-50">
            <Save size={15} /> Save exam
          </button>
        </div>
      </div>

      {/* Live summary */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="BOP" value={`${summary.bopPct}%`} tone={summary.bopPct >= 10 ? 'text-red-600' : 'text-emerald-600'} sub={`${summary.bop}/${summary.charted} sites`} />
        <Stat label="Sites ≥4mm" value={summary.s4} tone={summary.s4 > 0 ? 'text-amber-600' : 'text-brand-ink'} />
        <Stat label="Sites ≥6mm" value={summary.s6} tone={summary.s6 > 0 ? 'text-red-600' : 'text-brand-ink'} />
        <Stat label="Mean PD" value={summary.mean == null ? '—' : `${summary.mean}mm`} />
        <Stat label="Deepest" value={summary.deepest ? `${summary.deepest.mm}mm` : '—'} sub={summary.deepest ? `${summary.deepest.tooth} ${summary.deepest.site}` : ''} />
        <Stat label="Charted" value={summary.charted} sub="sites" />
      </div>

      {/* Grid */}
      <div className="overflow-x-auto rounded-xl border border-brand-border bg-white">
        <table className="min-w-[52rem] w-full border-collapse text-center text-xs">
          <thead className="bg-brand-surface text-[10px] uppercase tracking-wide text-brand-muted">
            <tr>
              <th className="sticky left-0 z-10 bg-brand-surface px-2 py-2 text-left">Tooth</th>
              <th colSpan={3} className="border-l border-brand-border px-1 py-1">Buccal PD</th>
              <th colSpan={3} className="border-l border-brand-border px-1 py-1">Lingual PD</th>
              <th className="border-l border-brand-border px-1 py-1">BOP</th>
              <th className="border-l border-brand-border px-1 py-1">Mob</th>
              <th className="border-l border-brand-border px-1 py-1">Fur</th>
            </tr>
          </thead>
          <ArchBody label="Upper" teeth={UPPER} data={data} setPd={setPd} toggleBop={toggleBop} setField={setField} />
          <ArchBody label="Lower" teeth={LOWER} data={data} setPd={setPd} toggleBop={toggleBop} setField={setField} />
        </table>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-semibold text-brand-ink">Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            placeholder="Diagnosis / recommendations (e.g. generalised moderate periodontitis; SRP quadrants; 3-month recall)"
            className="w-full rounded-xl border border-brand-border px-3 py-2 text-sm text-brand-ink" />
        </div>
        <div>
          <h2 className="mb-1 text-sm font-semibold text-brand-ink">Previous exams</h2>
          <div className="divide-y divide-brand-border/50 rounded-xl border border-brand-border bg-white">
            {exams.length === 0 && <p className="px-3 py-4 text-sm text-brand-muted">No previous perio exams.</p>}
            {exams.map((e) => (
              <Link key={e.id} href={`/perio_exams/${e.id}`}
                className={cn('flex items-center justify-between px-3 py-2 text-sm hover:bg-brand-surface', current?.id === e.id && 'bg-brand-primary/5')}>
                <span className="font-medium text-brand-ink">{new Date(e.examined_on).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                <span className="text-xs text-brand-muted">
                  BOP {e.summary?.bop_pct ?? 0}% · ≥4mm {e.summary?.sites_4plus ?? 0} · deepest {e.summary?.deepest?.mm ?? '—'}mm
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}

function ArchBody({ label, teeth, data, setPd, toggleBop, setField }) {
  return (
    <tbody>
      <tr><td colSpan={10} className="bg-brand-surface/40 px-2 py-1 text-left text-[10px] font-semibold uppercase tracking-wide text-brand-muted">{label} arch</td></tr>
      {teeth.map((n) => {
        const t = String(n)
        const td = data[t] || {}
        return (
          <tr key={t} className="border-t border-brand-border/50">
            <td className="sticky left-0 z-10 bg-white px-2 py-1 text-left font-semibold text-brand-ink">{t}</td>
            {SITES.map((site, i) => (
              <td key={site} className={cn('border-l px-0.5 py-0.5', i === 3 ? 'border-brand-border' : 'border-brand-border/30')}>
                <input inputMode="numeric" value={td.pd?.[site] ?? ''} onChange={(e) => setPd(t, site, e.target.value)}
                  title={`${t} ${site} probing depth (mm)`}
                  className={cn('h-7 w-8 rounded text-center text-xs outline-none focus:ring-2 focus:ring-brand-primary/40', pdTone(td.pd?.[site]))} />
              </td>
            ))}
            <td className="border-l border-brand-border px-1 py-1">
              <div className="flex items-center justify-center gap-0.5">
                {SITES.map((site) => (
                  <button key={site} type="button" onClick={() => toggleBop(t, site)}
                    title={`${t} ${site} bleeding on probing`}
                    className={cn('h-3.5 w-3.5 rounded-full border', td.bop?.[site] ? 'border-red-600 bg-red-500' : 'border-brand-border bg-white hover:bg-red-50')} />
                ))}
              </div>
            </td>
            <td className="border-l border-brand-border px-1 py-1">
              <select value={td.mob ?? ''} onChange={(e) => setField(t, 'mob', e.target.value)}
                className="rounded border border-brand-border bg-white px-1 py-0.5 text-xs" title="Mobility 0-3">
                <option value="">–</option>{[0, 1, 2, 3].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </td>
            <td className="border-l border-brand-border px-1 py-1">
              <select value={td.fur ?? ''} onChange={(e) => setField(t, 'fur', e.target.value)}
                className="rounded border border-brand-border bg-white px-1 py-0.5 text-xs" title="Furcation 0-4">
                <option value="">–</option>{[0, 1, 2, 3, 4].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </td>
          </tr>
        )
      })}
    </tbody>
  )
}

function Stat({ label, value, sub, tone = 'text-brand-ink' }) {
  return (
    <div className="rounded-xl border border-brand-border bg-white px-3 py-2.5">
      <p className="text-[10px] font-medium uppercase tracking-wide text-brand-muted">{label}</p>
      <p className={cn('text-lg font-semibold', tone)}>{value}</p>
      {sub ? <p className="text-[10px] text-brand-muted">{sub}</p> : null}
    </div>
  )
}
